import { EstateHotelRoomFeatures } from "@1games-ru/region-shared/dist/types/estate/estate";
import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import HotelCdn from "@/core/cdn/hotel/hotel-cdn";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import HotelCheck from "@/domains/hotel/components/HotelCheck";
import HotelCkeckbox from "@/domains/hotel/components/HotelCkeckbox";
import HotelRoomsInfo from "@/domains/hotel/components/HotelRoomsInfo";

type Props = {
  isCheck: boolean;
  src: string;
  uuid: string;
  level: string;
  price: number;
  isInactive: boolean;
  count: number;
  onClick: () => void;
  onHover: () => void;
  list: EstateHotelRoomFeatures;
};

export default function HotelSelectCard({
  isCheck,
  src,
  uuid,
  level,
  price,
  isInactive,
  count,
  onClick,
  onHover,
  list,
}: Props) {
  const { t } = useTranslation("hotel");

  const bgImage = uuid && src ? `url(${HotelCdn.getHotelImage(uuid, src)})` : "";

  if (!list) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn(
        "relative bg-dark-800/50 rounded-2xl transition-all duration-250",
        isInactive && "opacity-50",
      )}
      onClick={onClick}
      onMouseEnter={onHover}
    >
      <div
        className="absolute z-3 top-0 left-0 size-full rounded-2xl"
        style={
          isCheck
            ? {
                boxShadow: `
            inset 0 0 0 2px #368D44,
            inset 0 0 0 4px #121212
          `,
              }
            : {}
        }
      />
      <div className="relative w-[17rem] h-48 rounded-2xl overflow-clip">
        <div
          className="absolute top-0 left-0 w-full h-40"
          style={{
            backgroundImage: bgImage,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <div className="absolute top-0 left-0 size-full flex flex-col">
          <div className="absolute bottom-8 left-0 w-full h-26 bg-gradient-to-t from-dark-800 to-transparent" />
          <div className="absolute bottom-8 left-0 w-full h-25 bg-gradient-to-t from-dark-800 to-transparent" />
          <HotelCheck isCheck={isCheck} />
          <div className="relative z-1 flex justify-between items-center mt-auto mb-3.5 mx-5">
            <p className="text-18 font-bold leading-none">{t(`hotel.level.${level}`)}</p>
            <p className="text-16 font-bold leading-none">{formatPrice(price)} ₽</p>
          </div>
          <HotelRoomsInfo count={count} />
        </div>
      </div>
      <div className="relative flex flex-col gap-1.5 mt-3.5 pb-5 px-5">
        <div
          className={clsx(
            "absolute top-0 left-0 size-full rounded-2xl opacity-0 transition-all duration-200",
            !count &&
              "bg-[linear-gradient(180deg,rgba(24,24,24,0)_0%,rgba(171,41,41,0.1)_100%)] opacity-100",
            isCheck &&
              "bg-[linear-gradient(180deg,rgba(24,24,24,0)_0%,rgba(33,101,44,0.2)_100%)] opacity-100",
          )}
        />
        {Object.entries(list).map((item) => {
          if (item[0] === "garageCapacity") {
            return (
              <React.Fragment key={item[0]}>
                <HotelCkeckbox
                  id={item[0]}
                  checked
                  text={t(`hotel.${item[0]}`)}
                  isSecond={false}
                  disabled
                />
                <HotelCkeckbox
                  id="secondParking"
                  checked={Number(item[1]) > 1}
                  text={t("hotel.secondParking")}
                  isSecond
                  disabled
                />
              </React.Fragment>
            );
          }
          return (
            <HotelCkeckbox
              key={item[0]}
              id={item[0]}
              checked={!!item[1]}
              text={t(`hotel.${item[0]}`)}
              isSecond={item[0] === "foodDelivery"}
              disabled
            />
          );
        })}
      </div>
    </div>
  );
}
